import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Loader2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { ProductTranslations, type ProductTranslationsData } from "./ProductTranslations"
import { Button } from "@/components/ui/button"

interface ProductTranslationsModalProps {
  isOpen: boolean
  onClose: () => void
  productId: string
  merchantId: string
  skuIds: string[]
  defaultProductName: string
  defaultSkuNames: Record<string, string>
  defaultSkuNumbers?: Record<string, string>
  translations: ProductTranslationsData
  onChange: (translations: ProductTranslationsData) => void
  onSave?: () => Promise<void> | void
  savedLanguages?: string[]
}

export const ProductTranslationsModal: React.FC<ProductTranslationsModalProps> = ({
  isOpen,
  onClose,
  productId,
  merchantId: _merchantId,
  skuIds,
  defaultProductName,
  defaultSkuNames,
  defaultSkuNumbers,
  translations,
  onChange,
  onSave,
  savedLanguages = [],
}) => {
  const { t } = useTranslation()
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    if (!onSave) {
      // No backend persistence available here (e.g. ADD flow before the product exists).
      // Changes are already mirrored to the parent via onChange and will be persisted
      // together with the product on the main Save button.
      onClose()
      return
    }
    setIsSaving(true)
    try {
      await onSave()
      onClose()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isSaving) onClose()
      }}
    >
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t("productPage.translations")}</DialogTitle>
          <DialogDescription>
            Manage product name and variant translations for different languages.
          </DialogDescription>
        </DialogHeader>
        <div className="-mx-6 flex-1 overflow-y-auto px-6">
          <ProductTranslations
            productId={productId}
            skuIds={skuIds}
            defaultProductName={defaultProductName}
            defaultSkuNames={defaultSkuNames}
            defaultSkuNumbers={defaultSkuNumbers}
            translations={translations}
            onChange={onChange}
            savedLanguages={savedLanguages}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            {t("cancel")}
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t("save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
